import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { HTTPService } from "src/app/services/http.service";
import { StringUtils } from "src/app/system-utils/utils/StringUtils";
import { Agent } from "./agent.service";

/**
 * Um termo do dicionario canonico.
 *
 * O que faz o dicionario funcionar sao os SINONIMOS: e por eles que a pergunta
 * do usuario ("quero um laco") encontra o termo oficial ("ForEach") e o
 * identificador que o codigo espera ("FOREACH").
 */
export interface CanonicalTerm {
    _id?: string;
    /** o nome oficial, usado na resposta do agente e na expansao da busca */
    termoCanonico: string;
    /** como o usuario costuma falar; e o que faz o casamento acontecer */
    sinonimos: string[];
    /** uma linha, entra no glossario do prompt */
    definicao?: string;
    /** a ponte para o codigo (FOREACH, INNERFLOW); opcional */
    identificadorTecnico?: string;
    /** 'global' ou o agentName, para termos que so valem em um agente */
    escopo: string;
    /** 'toolbox' (importado) ou 'manual' (curadoria) - protege o import */
    origem: string;
    ativo: boolean;
    createdAt?: Date;
}

@Injectable({ providedIn: 'root' })
export class DictionaryService {

    constructor(private http: HTTPService) {}

    /** nome da colecao; cai no padrao 'dic' quando o agente ainda nao tem o campo */
    private nomeDaColecao(agent: Agent): string {

        if (agent.dicTable != null && agent.dicTable.length > 0) {
            return agent.dicTable;
        }

        return 'dic';
    }

    /**
     * Todos os termos do agente: os globais e os do proprio agente.
     * A tela mostra os dois juntos porque o Java tambem os usa juntos.
     */
    listarTermos(agent: Agent): Observable<any> {

        let filtro = {
            $or: [
                { escopo: 'global' },
                { escopo: agent.agentName }
            ]
        };

        let body = {
            "entityName": this.nomeDaColecao(agent),
            "useMongoManual": true,
            "type": "mongo",
            "dbName": agent.dbName,
            "maxRows": 500,
            "sql": StringUtils.stringifyMongoCondition(filtro)
        };

        return this.http.postGeneric<any>(body, "JsonBrokerServices/findnative", false, true);
    }

    /** grava um termo novo */
    criarTermo(agent: Agent, termo: CanonicalTerm): Observable<any> {

        let body = {
            "entityName": this.nomeDaColecao(agent),
            "useMongo": true,
            "dbName": agent.dbName,
            ...this.documentoDoTermo(termo)
        };

        return this.http.postGeneric<any>(body, "JsonBrokerServices/save", false, true);
    }

    /** grava as alteracoes de um termo existente */
    atualizarTermo(agent: Agent, termo: CanonicalTerm): Observable<any> {

        let body = {
            "entityName": this.nomeDaColecao(agent),
            "useMongo": true,
            "dbName": agent.dbName,
            ...this.documentoDoTermo(termo),
            "condition": `_id='${termo._id}'`
        };

        return this.http.postGeneric<any>(body, "JsonBrokerServices/save", false, true);
    }

    /** remove um termo de vez */
    removerTermo(agent: Agent, termo: CanonicalTerm): Observable<any> {

        let body = {
            "entityName": this.nomeDaColecao(agent),
            "useMongo": true,
            "dbName": agent.dbName,
            "condition": `_id=toobjectid('${termo._id}')`
        };

        return this.http.postGeneric<any>(body, "JsonBrokerServices/remove", false, true);
    }

    /**
     * Monta o documento que vai para o Mongo.
     * O _id fica de fora de proposito: ele viaja na condition, nao no corpo.
     */
    private documentoDoTermo(termo: CanonicalTerm): object {

        return {
            termoCanonico: termo.termoCanonico,
            sinonimos: termo.sinonimos ?? [],
            definicao: termo.definicao ?? '',
            identificadorTecnico: termo.identificadorTecnico ?? '',
            escopo: termo.escopo,
            origem: termo.origem,
            ativo: termo.ativo
        };
    }

    /**
     * Avisa o backend para descartar o cache do dicionario, que dura 30 minutos.
     * Sem isso, o termo recem-cadastrado so passaria a valer meia hora depois.
     *
     * O escopo decide o alcance: um termo GLOBAL entra no dicionario de todos os
     * agentes, entao limpar so a chave do agente aberto deixaria os outros com a
     * copia velha. Nesse caso mandamos sem agentName, que no Java significa
     * "limpar tudo".
     */
    descartarCache(escopo: string): void {

        let body = {};

        if (escopo !== 'global') {
            body = { "agentName": escopo };
        }

        this.http.postGeneric<any>(body, "AiServices/dictionary/clearCache", false, true).subscribe({
            next: () => {
                // nada a fazer: o termo ja foi gravado antes desta chamada
            },
            error: (erro) => {
                // de proposito nao incomoda o usuario: o termo FOI salvo, e o pior
                // caso e o dicionario levar os 30 minutos de sempre para valer
                console.error('Nao foi possivel descartar o cache do dicionario:', erro);
            }
        });
    }

}
